import React, { useState, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useGoals } from '../hooks/useGoals';
import { useBotStatus } from '../hooks/useBotStatus';
import {
  ShoppingCart,
  DollarSign,
  Target,
  Zap,
  Eye,
  X,
} from 'lucide-react';
import styled, { useTheme } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import { useVendas, useDashboardStats, queryKeys } from '../hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtimeUpdates } from '../hooks/useRealtimeUpdates';
import SalesChart from '../components/Sales/SalesChart';
import ProductPieChart from '../components/Sales/ProductPieChart';
import SalesTable from '../components/Sales/SalesTable';
import SkeletonLoader from '../components/SkeletonLoader';
import FadeInContent from '../components/FadeInContent';
import { useSkeletonDelay } from '../hooks/useSkeletonDelay';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  Button
} from '../components/Sales/SaleForm';
import { Venda } from '../types/api';
import { useDeleteVenda } from '../hooks/useApi';
import ConfirmationModal from '../components/Layout/ConfirmationModal'; // Importar o novo modal

// Utilitário para formatar moeda BRL, sem centavos se for zero
const formatCurrency = (value: number) => {
  const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  // Remove ,00 se não houver centavos
  return formatted.replace(',00', '');
};

const SalesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const SalesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[4]};
`;

const SalesTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const SalesInfo = styled.div`
  flex: 1;
`;

const SalesName = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => (props.theme.isLight ? props.theme.colors.textPrimary : props.theme.colors.textPrimary)};
  text-shadow: ${props => (props.theme.isLight ? 'none' : '0 2px 8px rgba(255,59,48,0.08)')};
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
`;

const SalesDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => (props.theme.isLight ? props.theme.colors.textTertiary : props.theme.colors.textSecondary)};
  margin: 0;
`;

const SalesActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;
  
  ${(props: { theme: DefaultTheme; $variant?: 'primary' | 'secondary' }) => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: ${props.theme.colors.primary};
          color: ${props.theme.colors.textPrimary};
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.neon};
            background: ${props.theme.colors.primaryHover};
            color: ${props.theme.colors.textPrimary};
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundTertiary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundSecondary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[4]};
  text-align: center;
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.easeInOut};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const StatValue = styled.div`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${props => props.theme.spacing[6]};
  margin-bottom: ${props => props.theme.spacing[6]};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[6]};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Sales: React.FC = () => {
  const theme = useTheme();
  const [viewingVenda, setViewingVenda] = useState<Venda | null>(null);
  const [deletingVenda, setDeletingVenda] = useState<Venda | null>(null);
  // Modal de Metas com contexto global
  const [isGoalsModalOpen, setIsGoalsModalOpen] = useState(false);
  const { metaVendas, metaReceita, setMetaVendas, setMetaReceita } = useGoals();
  // vendasPagas e totalVendas precisam ser declarados antes
  const deleteVendaMutation = useDeleteVenda();
  const [chartPeriod, setChartPeriod] = useState('30d');

  const { isLoading: botLoading, isActive: botActive, error: botError } = useBotStatus();
  const { data: vendasData, isLoading: vendasLoading } = useVendas();
  const { data: dashboardStats, isLoading: dashboardStatsLoading } = useDashboardStats();
  const queryClient = useQueryClient();

  // Realtime updates
  const handleRealtimeEvent = React.useCallback((event: { type: string; payload: unknown }) => {
    const t = event.type;
    if (t && (t === 'venda_created' || t === 'venda_updated' || t === 'venda_deleted' || t === 'dashboard_update' || t === 'lead_update' || t === 'lead_created' || t === 'lead_deleted')) {
      const keys = [queryKeys.vendas, queryKeys.vendasTotal, queryKeys.dashboardStats];
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: key });
        queryClient.refetchQueries({ queryKey: key, type: 'active' });
      }
    }
  }, [queryClient]);
  useRealtimeUpdates({ onEvent: handleRealtimeEvent });

  // Todas as vendas para tabela
  const vendas = useMemo(() => vendasData?.data?.vendas || [], [vendasData]);
  // Apenas vendas pagas para os cards e gráficos
  const vendasPagas = useMemo(() => vendas.filter(v => v.status?.toLowerCase() === 'pago'), [vendas]);
  const vendasRealizadas = vendasPagas.length;
  const totalVendas = useMemo(() => {
    if (!vendasPagas || vendasPagas.length === 0) return 0;
    return vendasPagas.reduce((sum, venda) => {
      return sum + (parseFloat(venda.valor.replace('R$', '').replace(',', '.')) || 0);
    }, 0);
  }, [vendasPagas]);
  const receitaAtual = totalVendas;
  const progressoVendas = metaVendas > 0 ? Math.min(100, Math.round((vendasRealizadas / metaVendas) * 100)) : 0;
  const progressoReceita = metaReceita > 0 ? Math.min(100, Math.round((receitaAtual / metaReceita) * 100)) : 0;

  // Processar dados reais de vendas para gráficos
  const salesData = useMemo(() => {
  if (!vendasPagas || vendasPagas.length === 0) return [];
  // Agrupar vendas pagas por dia/mês/ano
  const dailyData = vendasPagas.reduce((acc: Record<string, { vendas: number; receita: number }>, venda: Venda) => {
      const d = new Date(venda.dataVenda);
      const dia = String(d.getDate()).padStart(2, '0');
      const mes = String(d.getMonth() + 1).padStart(2, '0');
      const ano = d.getFullYear();
      const key = `${dia}/${mes}/${ano}`;
      if (!acc[key]) {
        acc[key] = { vendas: 0, receita: 0 };
      }
      acc[key].vendas += 1;
      acc[key].receita += parseFloat(venda.valor.replace('R$', '').replace(',', '.')) || 0;
      return acc;
    }, {});
    return Object.entries(dailyData).map(([name, data]) => ({
      name,
      vendas: data.vendas,
      receita: data.receita
    }));
  }, [vendasPagas]);

  const productData = useMemo(() => {
  if (!vendasPagas || vendasPagas.length === 0) return [];
  const productCounts = vendasPagas.reduce((acc: Record<string, number>, venda: Venda) => {
      const produto = venda.produto || 'Produto Desconhecido';
      acc[produto] = (acc[produto] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(productCounts).map(([name, count]) => ({
      name,
      value: count,
      vendas: count
    }));
  }, [vendasPagas]);

  const statusData = useMemo(() => {
    if (!vendas || vendas.length === 0) return [];
    
      const statusCounts = vendas.reduce((acc: Record<string, number>, venda: Venda) => {
        const status = venda.status || 'Pendente';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const total = Object.values(statusCounts).reduce((sum: number, count: number) => sum + count, 0);
    
      return Object.entries(statusCounts).map(([name, count]) => ({
        name,
        value: Math.round((count / total) * 100),
        vendas: count
      }));
  }, [vendas]);

  const conversionData = useMemo(() => {
    const data = dashboardStats?.data;
    return [
      { name: 'Novo', valor: data?.leadsNovo || 0 },
      { name: 'Qualificado', valor: data?.leadsQualificado || 0 },
      { name: 'Prospect', valor: data?.leadsProspect || 0 },
      { name: 'Convertido', valor: data?.leadsConvertido || 0 },
      { name: 'Perdido', valor: data?.leadsPerdido || 0 },
    ];
  }, [dashboardStats]);

  const handleEditVenda = (venda: Venda) => {
    // TODO: implement edit sale page
  };

  const handleDeleteRequest = (venda: Venda) => {
    setDeletingVenda(venda);
  };

  const handleConfirmDelete = async () => {
    if (!deletingVenda) return;

    try {
      await deleteVendaMutation.mutateAsync(deletingVenda.id);
    } catch (error) {
      console.error("Erro ao excluir venda:", error);
    } finally {
      setDeletingVenda(null); // Fecha o modal
    }
  };

  const handleViewVenda = (venda: Venda) => {
    setViewingVenda(venda);
  };

  // Hook para adicionar delay mínimo no skeleton (suavização)
  const showSkeleton = useSkeletonDelay(
    botLoading || !botActive || !!botError || vendasLoading || dashboardStatsLoading,
    1500
  );

  if (showSkeleton) {
    return (
      <SalesContainer>
        <SkeletonLoader variant="sales" />
      </SalesContainer>
    );
  }

  return (
    <FadeInContent>
      <SalesContainer>
      <SalesHeader>
        <SalesTitle>
          <SalesInfo>
            <SalesName>Centro de Vendas</SalesName>
            <SalesDescription>Monitore performance e receita</SalesDescription>
          </SalesInfo>
        </SalesTitle>
        <SalesActions>
          <ActionButton $variant="secondary" onClick={() => setIsGoalsModalOpen(true)}>
            <Target size={16} />
            Metas
          </ActionButton>
      {/* Modal de Metas */}
      <AnimatePresence>
        {isGoalsModalOpen && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsGoalsModalOpen(false)}
            style={{ zIndex: 1000 }}
          >
            <ModalContent
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 420, width: '96vw', padding: 0, overflow: 'visible' }}
            >
              <ModalHeader style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing[2], marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Target size={26} color={theme.colors.primary} style={{ filter: 'drop-shadow(0 0 6px #ff3b30aa)' }} />
                  <ModalTitle style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: 0.6, color: theme.colors.primary, textShadow: theme.isLight ? 'none' : '0 1px 8px #ff3b3016' }}>Metas do Mês</ModalTitle>
                </div>
                <button onClick={() => setIsGoalsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textSecondary, fontSize: 20, lineHeight: 1, padding: 0 }}>&times;</button>
              </ModalHeader>
              <ModalBody style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[5], padding: '0 1.5rem 1.5rem 1.5rem' }}>
                <div style={{ background: theme.isLight ? '#fafafa' : 'rgba(255,255,255,0.01)', border: `1.2px solid ${theme.colors.borderLight}`, borderRadius: 10, padding: 12, boxShadow: theme.isLight ? '0 1px 6px rgba(16,24,32,0.03)' : '0 1px 8px 0 rgba(255,59,48,0.06)' }}>
                  <label style={{ fontWeight: 800, color: theme.colors.textPrimary, fontSize: 17, letterSpacing: 0.7, textTransform: 'uppercase', marginBottom: 2, display: 'block' }}>Meta de Vendas</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Ex: 100"
                    value={metaVendas === 0 ? '' : metaVendas}
                    onChange={e => {
                      let val = e.target.value.replace(/\D/g, '');
                      if (val.length > 5) val = val.slice(0, 5); // Limite de 5 dígitos
                      setMetaVendas(val === '' ? 0 : Number(val));
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: `1.2px solid ${theme.colors.primary}`,
                      marginTop: 8,
                      fontSize: 15,
                      background: theme.isLight ? theme.colors.backgroundSecondary : '#19191c',
                      color: theme.colors.textPrimary,
                      outline: 'none',
                      fontWeight: 700,
                      boxShadow: theme.isLight ? '0 1px 6px 0 rgba(16,24,32,0.04)' : '0 1px 6px 0 rgba(255,59,48,0.06)',
                      MozAppearance: 'textfield',
                      appearance: 'textfield',
                      transition: 'border 0.15s, box-shadow 0.15s',
                    }}
                    onFocus={e => e.currentTarget.style.border = `1.5px solid ${theme.colors.primary}`}
                    onBlur={e => e.currentTarget.style.border = `1.5px solid ${theme.colors.primary}`}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "+", "-", ",", "."].includes(e.key)) e.preventDefault();
                      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown')) e.preventDefault();
                    }}
                  />
                  <div style={{ marginTop: 12, fontSize: 15, color: theme.colors.textSecondary, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShoppingCart size={18} color={theme.colors.success} />
                    Realizadas: <b style={{ color: theme.colors.success }}>{vendasRealizadas}</b> / {metaVendas > 0 ? metaVendas : '--'}
                  </div>
                  <div style={{ background: theme.isLight ? theme.colors.backgroundTertiary : '#232526', borderRadius: 8, height: 12, marginTop: 10, overflow: 'hidden', boxShadow: theme.isLight ? 'none' : '0 1px 6px 0 rgba(255,59,48,0.06)' }}>
                    <motion.div style={{ height: 14, background: theme.colors.primary, width: `${progressoVendas}%`, borderRadius: 8, boxShadow: progressoVendas > 0 ? (theme.isLight ? '0 0 8px 0 rgba(255,91,78,0.12)' : '0 0 8px 0 #ff3b3044') : 'none', transition: 'background 0.2s' }} initial={{ width: 0 }} animate={{ width: `${progressoVendas}%` }} transition={{ duration: 0.7 }} />
                  </div>
                  <div style={{ fontSize: 13, color: progressoVendas >= 100 ? theme.colors.success : theme.colors.textSecondary, marginTop: 6, fontWeight: 700, letterSpacing: 0.6 }}>
                    {metaVendas > 0 ? (progressoVendas >= 100 ? 'Meta atingida!' : `${progressoVendas}% da meta`) : '--'}
                  </div>
                </div>
                <div style={{ background: theme.isLight ? theme.colors.backgroundSecondary : 'rgba(255,255,255,0.01)', border: `1.2px solid ${theme.colors.borderLight}`, borderRadius: 10, padding: 12, boxShadow: theme.isLight ? '0 1px 6px rgba(16,24,32,0.03)' : '0 1px 8px 0 rgba(255,59,48,0.06)' }}>
                  <label style={{ fontWeight: 800, color: theme.colors.textPrimary, fontSize: 17, letterSpacing: 0.7, textTransform: 'uppercase', marginBottom: 2, display: 'block' }}>Meta de Receita (R$)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Ex: 50000"
                    value={metaReceita === 0 ? '' : metaReceita}
                    onChange={e => {
                      let val = e.target.value.replace(/\D/g, '');
                      if (val.length > 9) val = val.slice(0, 9); // Limite de 9 dígitos para receita
                      setMetaReceita(val === '' ? 0 : Number(val));
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: `1.2px solid ${theme.colors.primary}`,
                      marginTop: 8,
                      fontSize: 15,
                      background: theme.isLight ? theme.colors.backgroundSecondary : '#19191c',
                      color: theme.colors.textPrimary,
                      outline: 'none',
                      fontWeight: 700,
                      boxShadow: theme.isLight ? '0 1px 6px 0 rgba(16,24,32,0.04)' : '0 1px 6px 0 rgba(255,59,48,0.06)',
                      MozAppearance: 'textfield',
                      appearance: 'textfield',
                      transition: 'border 0.15s, box-shadow 0.15s',
                    }}
                    onFocus={e => e.currentTarget.style.border = `1.5px solid ${theme.colors.primary}`}
                    onBlur={e => e.currentTarget.style.border = `1.5px solid ${theme.colors.primary}`}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "+", "-", ",", "."].includes(e.key)) e.preventDefault();
                      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown')) e.preventDefault();
                    }}
                  />
                  <div style={{ marginTop: 12, fontSize: 15, color: theme.colors.textSecondary, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DollarSign size={18} color={theme.colors.primary} />
                    Atual: <b style={{ color: theme.colors.primary }}>R$ {receitaAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</b> / R$ {metaReceita > 0 ? metaReceita.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '--'}
                  </div>
                  <div style={{ background: theme.isLight ? theme.colors.backgroundTertiary : '#232526', borderRadius: 8, height: 12, marginTop: 10, overflow: 'hidden', boxShadow: theme.isLight ? 'none' : '0 1px 6px 0 rgba(255,59,48,0.06)' }}>
                    <motion.div style={{ height: 14, background: theme.colors.primary, width: `${progressoReceita}%`, borderRadius: 8, boxShadow: progressoReceita > 0 ? (theme.isLight ? '0 0 8px 0 rgba(255,91,78,0.12)' : '0 0 8px 0 #ff3b3044') : 'none', transition: 'background 0.2s' }} initial={{ width: 0 }} animate={{ width: `${progressoReceita}%` }} transition={{ duration: 0.7 }} />
                  </div>
                  <div style={{ fontSize: 13, color: progressoReceita >= 100 ? theme.colors.success : theme.colors.textSecondary, marginTop: 6, fontWeight: 700, letterSpacing: 0.6 }}>
                    {metaReceita > 0 ? (progressoReceita >= 100 ? 'Meta atingida!' : `${progressoReceita}% da meta`) : '--'}
                  </div>
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
          <Link to="/sales/new" style={{ textDecoration: 'none' }}>
            <ActionButton $variant="primary">
              <Zap size={16} />
              Nova Venda
            </ActionButton>
          </Link>
        </SalesActions>
      </SalesHeader>

  {/* Stats Cards */}
      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0 }}
        >
          <StatValue>{vendasPagas.length}</StatValue>
          <StatLabel>Vendas Realizadas</StatLabel>
        </StatCard>
        
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <StatValue>{formatCurrency(totalVendas)}</StatValue>
          <StatLabel>Receita Total</StatLabel>
        </StatCard>
        
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <StatValue>{dashboardStats?.data?.taxaConversao || 0}%</StatValue>
          <StatLabel>Taxa de Conversão</StatLabel>
          {/* Percentual removido */}
        </StatCard>
        
  {/* Card de Ticket Médio removido */}
      </StatsGrid>

      {/* Charts Section */}
      <ChartsGrid>
        <SalesChart
          title="Vendas por Período"
          data={salesData}
          type="area"
          dataKey="vendas"
          xAxisKey="name"
          showControls={true}
          controls={[
            { label: '7 dias', value: '7d' },
            { label: '30 dias', value: '30d' },
            { label: '90 dias', value: '90d' }
          ]}
          activeControl={chartPeriod}
          onControlChange={setChartPeriod}
        />
        
        <SalesChart
          title="Status das Vendas"
          data={statusData}
          type="pie"
          dataKey="vendas"
        />
      </ChartsGrid>

      <ChartsRow>
        <ProductPieChart
          title="Vendas por Produto"
          data={productData.map(p => ({ name: p.name, value: p.value }))}
        />
        
        <SalesChart
          title="Funil de Conversão"
          data={conversionData}
          type="line"
          dataKey="valor"
          xAxisKey="name"
        />
      </ChartsRow>

      {/* Sales Table */}
      <SalesTable
        vendas={vendas}
        loading={vendasLoading}
        onEdit={handleEditVenda}
        onDelete={handleDeleteRequest} // Usar a função que abre o modal
        onView={handleViewVenda}
        onCreate={() => {}}
      />

      {/* Modal Visualizar Venda */}
      {viewingVenda && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setViewingVenda(null)}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <Eye size={20} />
                Visualizar Venda
              </ModalTitle>
              <CloseButton onClick={() => setViewingVenda(null)}>
                <X size={16} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div><b>Cliente:</b> {viewingVenda.cliente}</div>
                <div><b>Produto:</b> {viewingVenda.produto}</div>
                <div>
                  <b>Valor:</b> R$ {viewingVenda.valor}
                </div>
                <div><b>Status:</b> {viewingVenda.status}</div>
                <div><b>Data:</b> {(() => {
                  const dataStr = new Date(viewingVenda.dataVenda).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                  const [data, hora] = dataStr.replace(',', '').split(' ');
                  return `${data} às ${hora}`;
                })()}</div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button type="button" onClick={() => setViewingVenda(null)}>
                Fechar
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmationModal
        isOpen={!!deletingVenda}
        onClose={() => setDeletingVenda(null)}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message={
          <>
            Tem certeza que deseja excluir a venda{" "}
            <strong>#{deletingVenda?.id}</strong>? Esta ação não pode ser
            desfeita.
          </>
        }
        confirmText="Excluir"
        loading={deleteVendaMutation.isPending}
      />
    </SalesContainer>
    </FadeInContent>
  );
};

export default Sales;

